import { useMemo, useState } from 'react';
import type { MatterParty, RoleTemplate } from '../../api';
import { matterApi } from '../../api';
import AddPartyModal from './AddPartyModal';

type Props = {
  matterId: number | string;
  roles: RoleTemplate[];
  parties: MatterParty[];
  onChange: () => void;
};

const ORDINAL = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th'];
const ordinal = (n: number) => ORDINAL[n - 1] || `${n}th`;

const ENTITY_LABELS: Record<string, string> = {
  natural_person:    'Natural person',
  juristic_person:   'Juristic person',
  state_organ:       'Organ of state (department / ministry)',
  state_owned_entity: 'State-owned entity (SOE)',
  trust:             'Trust',
  partnership:       'Partnership',
  sole_proprietor:   'Sole proprietor',
  deceased_estate:   'Deceased estate',
  minor_assisted:    'Minor (assisted)',
  other:             'Other',
};

function initials(name?: string | null) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = parts[0][0];
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase();
}

export default function PartiesPanel({ matterId, roles, parties, onChange }: Props) {
  const [adding, setAdding] = useState<{ roleCode?: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const grouped = useMemo(() => {
    const byRole = new Map<string, MatterParty[]>();
    for (const p of parties) {
      const list = byRole.get(p.role_code) || [];
      list.push(p);
      byRole.set(p.role_code, list);
    }
    return byRole;
  }, [parties]);

  // Dedupe by role_code defensively — the catalog server already dedupes, but
  // keeping this guard means a stale tenant with duplicate party_role_templates
  // rows never renders two sections for the same role.
  const sortedRoles = (() => {
    const seen = new Set<string>();
    const uniq: RoleTemplate[] = [];
    for (const r of [...roles].sort((a, b) => a.display_order - b.display_order)) {
      if (seen.has(r.role_code)) continue;
      seen.add(r.role_code);
      uniq.push(r);
    }
    return uniq;
  })();

  const withdraw = async (pid: number | string) => {
    if (!confirm('Withdraw this party from the matter?')) return;
    setBusy(String(pid));
    try { await matterApi.withdrawParty(matterId, pid as number); onChange(); }
    finally { setBusy(null); }
  };
  const remove = async (pid: number | string) => {
    if (!confirm('Delete this party row? (The contact remains in the parties book.)')) return;
    setBusy(String(pid));
    try { await matterApi.removeParty(matterId, pid as number); onChange(); }
    finally { setBusy(null); }
  };
  const toggleClient = async (p: MatterParty) => {
    setBusy(String(p.matter_party_id));
    try { await matterApi.updateParty(matterId, p.matter_party_id, { is_my_client: !p.is_my_client }); onChange(); }
    finally { setBusy(null); }
  };

  return (
    <div className="stack">
      {sortedRoles.map((role) => {
        const rows = (grouped.get(role.role_code) || []).sort((a, b) => a.role_order - b.role_order);
        const needsMore = role.min_count > 0 && rows.length < role.min_count;
        const canAdd    = !role.max_count || rows.length < role.max_count;

        return (
          <section key={role.role_code} className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <header style={sectionHeader}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flex: 1, minWidth: 0 }}>
                <h3 style={{ margin: 0 }}>
                  {rows.length > 1 || role.is_numbered ? role.role_label_plural : role.role_label_singular}
                </h3>
                <span className="muted" style={{ fontSize: 12 }}>
                  {rows.length} {rows.length === 1 ? 'party' : 'parties'}
                </span>
                {needsMore && (
                  <span className="pill danger">
                    {role.min_count - rows.length} more required
                  </span>
                )}
                <span className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  {role.side.replace('_', ' ')}
                </span>
              </div>
              {canAdd && (
                <button onClick={() => setAdding({ roleCode: role.role_code })}>
                  + Add {role.role_label_singular.toLowerCase()}
                </button>
              )}
            </header>

            {rows.length === 0 ? (
              <div style={emptyRow}>
                <span className="muted">No {role.role_label_plural.toLowerCase()} yet.</span>
              </div>
            ) : (
              <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                {rows.map((p) => {
                  const pid = p.matter_party_id;
                  const isBusy = busy === String(pid);
                  const entityLabel = p.entity_type ? ENTITY_LABELS[p.entity_type] || p.entity_type : '—';
                  return (
                    <li key={pid} style={{
                      ...partyRow,
                      opacity: p.left_at ? 0.6 : 1,
                    }}>
                      <div style={avatar}>{initials(p.display_name)}</div>
                      {role.is_numbered && rows.length > 1 && (
                        <span className="muted" style={{ fontSize: 12, minWidth: 32 }}>
                          {ordinal(p.role_order)}
                        </span>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {p.display_name || <span className="muted">(name missing)</span>}
                        </div>
                        <div className="muted" style={{ fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {entityLabel}
                          {p.email ? ` · ${p.email}` : ''}
                          {p.left_at ? ` · withdrawn ${p.left_at}` : ''}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleClient(p)}
                        disabled={isBusy || !!p.left_at}
                        className={p.is_my_client ? '' : 'ghost'}
                        style={{
                          padding: '4px 10px',
                          fontSize: 12,
                          borderRadius: 999,
                          background: p.is_my_client ? 'var(--color-accent-soft)' : 'transparent',
                          borderColor: p.is_my_client ? 'var(--color-accent)' : 'var(--color-border)',
                          color: p.is_my_client ? 'var(--color-accent)' : 'var(--color-text-muted)',
                          fontWeight: p.is_my_client ? 600 : 400,
                          whiteSpace: 'nowrap',
                        }}
                        title={p.is_my_client ? 'This is my client — click to unset' : 'Mark as my client'}
                      >
                        {p.is_my_client ? '★ My client' : '☆ Mark as client'}
                      </button>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        {!p.left_at && (
                          <button
                            className="ghost"
                            onClick={() => withdraw(pid)}
                            disabled={isBusy}
                            style={{ padding: '6px 10px', fontSize: 13 }}
                          >
                            Withdraw
                          </button>
                        )}
                        <button
                          className="danger"
                          onClick={() => remove(pid)}
                          disabled={isBusy}
                          style={{ padding: '6px 10px', fontSize: 13 }}
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      })}

      {adding && (
        <AddPartyModal
          matterId={matterId}
          roles={roles}
          defaultRoleCode={adding.roleCode}
          onClose={() => setAdding(null)}
          onAdded={() => { setAdding(null); onChange(); }}
        />
      )}
    </div>
  );
}

const sectionHeader: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '14px 18px',
  borderBottom: '1px solid var(--color-border)',
  background: 'var(--color-surface-2)',
};

const partyRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '14px 18px',
  borderBottom: '1px solid var(--color-border)',
};

const emptyRow: React.CSSProperties = {
  padding: '18px',
  fontSize: 13,
};

const avatar: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: '50%',
  background: 'var(--color-accent-soft)',
  color: 'var(--color-accent)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 600,
  fontSize: 12,
  flexShrink: 0,
  letterSpacing: '0.02em',
};
